import React, { useMemo } from 'react';
import MDPagination from "components/atoms/MDPagination/MDPagination";
import MDInput from "components/atoms/MDInput/MDInput";

import {
  StyledChevronIcon,
  StyledPaginationContainer,
  StyledEntriesInfo,
  StyledEntriesText,
  StyledPaginationInputContainer
} from "./PaginationControls.styled";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  showTotalEntries?: boolean;
  isLoading?: boolean;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPreviousPage,
  onNextPage,
  showTotalEntries = false,
  isLoading = false
}) => {
  const paginationInfo = useMemo(() => {
    const canPreviousPage = currentPage > 0;
    const canNextPage = currentPage < totalPages - 1;
    const entriesStart = currentPage === 0 ? 1 : currentPage * pageSize + 1;
    const entriesEnd = Math.min((currentPage + 1) * pageSize, totalCount || (currentPage + 1) * pageSize);
    return {
      canPreviousPage,
      canNextPage,
      entriesStart,
      entriesEnd
    };
  }, [currentPage, totalPages, pageSize, totalCount]);

  const renderPagination = () => {
    const pageOptions = Array.from({ length: totalPages }, (_, i) => i);

    return pageOptions.map((option: number) => (
      <MDPagination
        item
        key={option}
        onClick={() => !isLoading && onPageChange(option)}
        active={currentPage === option}
      >
        {option + 1}
      </MDPagination>
    ));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const pageNumber = parseInt(event.target.value) - 1;
    if (!isNaN(pageNumber) && pageNumber >= 0 && pageNumber < totalPages) {
      onPageChange(pageNumber);
    }
  };

  if (totalPages <= 1) {
    return showTotalEntries && totalCount ? (
      <StyledPaginationContainer showTotalEntries={showTotalEntries} totalPages={totalPages}>
        <StyledEntriesInfo>
          <StyledEntriesText variant="button">
            Showing {paginationInfo.entriesStart} to {paginationInfo.entriesEnd} of {totalCount} entries
          </StyledEntriesText>
        </StyledEntriesInfo>
      </StyledPaginationContainer>
    ) : null;
  }

  return (
    <StyledPaginationContainer showTotalEntries={showTotalEntries} totalPages={totalPages}>
      {showTotalEntries && totalCount && (
        <StyledEntriesInfo>
          <StyledEntriesText variant="button">
            Showing {paginationInfo.entriesStart} to {paginationInfo.entriesEnd} of {totalCount} entries
          </StyledEntriesText>
        </StyledEntriesInfo>
      )}

      <MDPagination>
        {paginationInfo.canPreviousPage && (
          <MDPagination item onClick={() => !isLoading && onPreviousPage()}>
            <StyledChevronIcon>chevron_left</StyledChevronIcon>
          </MDPagination>
        )}

        {totalPages > 6 ? (
          <StyledPaginationInputContainer>
            <MDInput
              inputProps={{
                type: "number",
                min: 1,
                max: totalPages,
                disabled: isLoading
              }}
              value={currentPage + 1}
              onChange={handleInputChange}
            />
          </StyledPaginationInputContainer>
        ) : (
          renderPagination()
        )}

        {paginationInfo.canNextPage && (
          <MDPagination item onClick={() => !isLoading && onNextPage()}>
            <StyledChevronIcon>chevron_right</StyledChevronIcon>
          </MDPagination>
        )}
      </MDPagination>
    </StyledPaginationContainer>
  );
};

export default PaginationControls;